import type { ReactNode } from 'react'
import { ShieldCheck, ShieldX, AlertTriangle, Info } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import type { AgentMessage as AgentMessageType } from '@/hooks/useClaudeAgent'
import { AGENT_DISPLAY_NAME } from '@/data/agentBranding'
import AgentAskBubble from '@/components/agent/AgentAskBubble'
import { formatSpend } from '@/store/creditsStore'

// The CLI agents emit a fenced ```twasta:ask block when they want to ask the
// user a structured question. The block is surfaced separately as an `ask`
// message, so strip it out of the streamed assistant text it rode in on.
function stripAskBlock(text: string): string {
  if (!text || !text.includes('twasta:ask')) return text || ''
  return text.replace(/```+\s*twasta:ask\b[\s\S]*?```+/g, '').replace(/twasta:ask/g, '').trim()
}

const TOOL_LABELS: Record<string, string> = {
  Read: 'Reading',
  Write: 'Writing',
  Edit: 'Editing',
  Glob: 'Searching files',
  Grep: 'Searching content',
  Bash: 'Running command',
}

const TOOL_ICONS: Record<string, string> = {
  Write: 'edit_document',
  Edit: 'edit_note',
  Bash: 'terminal',
  NotebookEdit: 'description',
}

/** "1m 23s · 12.4k tokens · 21 credits" from an activity message's accounting.
 *  Cost arrives from the provider in dollars and is shown in credits — the unit
 *  the platform charges in, and the only one a user ever sees. */
function formatActivityMeta(meta?: { elapsedS?: number; tokens?: number; costUsd?: number }): string {
  if (!meta) return ''
  const parts: string[] = []
  if (meta.elapsedS != null) {
    parts.push(
      meta.elapsedS >= 60
        ? `${Math.floor(meta.elapsedS / 60)}m ${Math.round(meta.elapsedS % 60)}s`
        : `${meta.elapsedS}s`,
    )
  }
  if (meta.tokens != null) {
    parts.push(
      meta.tokens >= 1000
        ? `${(meta.tokens / 1000).toFixed(1)}k tokens`
        : `${meta.tokens} tokens`,
    )
  }
  const spend = formatSpend(meta.costUsd)
  if (spend) parts.push(spend)
  return parts.join(' · ')
}

interface AgentMessageProps {
  message: AgentMessageType
  onPermissionResponse?: (permissionId: string, decision: 'allow' | 'deny') => void
  /** Called when the user answers a clarifying-question (`ask`) bubble.
   *  ``connectionId`` is present when the answer came from the connection
   *  form (the exact connection the user picked). */
  onAnswer?: (answer: string, connectionId?: string) => void
  /** Accessible name for an assistant reply. Defaults to the product's own
   *  agent name — which CLI engine runs behind it is a Settings choice, not
   *  something the reply should be branded with. */
  agentLabel?: string
  /** Project folder — lets a connection-kind ask bubble open the connection
   *  editor and save into this project. */
  project?: string
}

/** A user's own message: a soft accent bubble on the right, no "YOU" caption. */
export function UserBubble({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <div
        style={{
          maxWidth: '86%',
          padding: '0.625rem 0.875rem',
          borderRadius: '14px 14px 4px 14px',
          background: 'var(--color-accent-weak)',
          color: 'var(--color-text)',
          fontSize: '1.0625rem',
          lineHeight: 1.55,
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
        }}
      >
        {children}
      </div>
    </div>
  )
}

/** An agent reply: plain markdown at reading size, no box and no caption. */
export function AssistantText({ text, label = AGENT_DISPLAY_NAME }: { text: string; label?: string }) {
  return (
    <div
      className="markdown-content askai-reply"
      aria-label={`${label} reply`}
      style={{ fontSize: '1.0625rem', lineHeight: 1.6, color: 'var(--color-text)', overflowWrap: 'anywhere' }}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
    </div>
  )
}

export default function AgentMessage({ message, onPermissionResponse, onAnswer, agentLabel = AGENT_DISPLAY_NAME, project }: AgentMessageProps) {

  // Clarifying-question bubble (agent asked instead of guessing).
  if (message.role === 'ask' && message.ask) {
    return (
      <AgentAskBubble
        question={message.ask.question}
        options={message.ask.options}
        onAnswer={(ans, connectionId) => onAnswer?.(ans, connectionId)}
        answered={!!message.askAnswered}
        answeredWith={message.askAnsweredWith ?? null}
        connectionForm={message.ask.kind === 'connection'}
        // Every recognised kind exists because its answer is a SECRET: the
        // platform collects it and the agent is handed back only an id.
        formKind={message.ask.kind}
        appId={message.ask.app_id}
        seedJson={message.ask.seed_json}
        project={project}
      />
    )
  }

  // Permission request message
  if (message.role === 'permission' && message.permissionRequest) {
    const perm = message.permissionRequest
    const decided = message.permissionDecision
    const isApproved = decided === 'allow'
    const toolIcon = TOOL_ICONS[perm.tool_name] || 'security'

    // Plain-language summary of what the agent wants to do. The CLI's Bash tool
    // ships a `description` (active-voice "what this command does"); other tools
    // may carry one too. Surface it so the user isn't left reading raw shell.
    const rawDesc = perm.tool_input?.description
    const friendlyDesc = typeof rawDesc === 'string' ? rawDesc.trim() : ''

    const tone = decided
      ? (isApproved ? 'var(--color-success)' : 'var(--color-error)')
      : 'var(--color-warning)'
    const toneWeak = decided
      ? (isApproved ? 'var(--color-success-weak)' : 'var(--color-error-weak)')
      : 'var(--color-warning-weak)'

    return (
      <div style={{
        padding: '0.875rem 1rem',
        borderRadius: 'var(--radius-lg)',
        background: toneWeak,
        border: `1px solid color-mix(in srgb, ${tone} 45%, transparent)`,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span className="material-icons" aria-hidden="true" style={{ fontSize: 19, color: tone }}>
            {toolIcon}
          </span>
          <span style={{ fontSize: '1.0625rem', fontWeight: 600, color: 'var(--color-text)' }}>
            {decided
              ? (isApproved
                  ? 'You allowed this step'
                  : message.permissionExpired
                    ? 'No answer in time — the agent skipped this step. Ask it to try again.'
                    : 'You blocked this step')
              : 'The agent needs your permission'}
          </span>
        </div>

        <div style={{ fontSize: '1rem', color: 'var(--color-text)', lineHeight: 1.5 }}>
          {friendlyDesc || `It wants to use ${perm.tool_name}.`}
        </div>

        {perm.detail && (() => {
          const cmdBox = (
            <div style={{
              padding: '0.5rem 0.625rem',
              background: 'var(--color-input-bg)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              fontFamily: 'var(--font-mono)',
              fontSize: '0.875rem',
              color: 'var(--color-text)',
              wordBreak: 'break-all',
              whiteSpace: 'pre-wrap',
              maxHeight: 140,
              overflow: 'auto',
            }}>
              {perm.detail}
            </div>
          )
          // With a plain-language description leading, tuck the raw command
          // behind a disclosure so it's available but not the headline. Without
          // one, show the command directly.
          return friendlyDesc ? (
            <details>
              <summary style={{
                cursor: 'pointer',
                fontSize: '0.9375rem',
                color: 'var(--color-text-secondary)',
                marginBottom: '0.375rem',
                userSelect: 'none',
              }}>
                Show the exact command
              </summary>
              {cmdBox}
            </details>
          ) : cmdBox
        })()}

        {decided ? (
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.375rem',
            fontSize: '0.9375rem',
            fontWeight: 600,
            color: tone,
          }}>
            {isApproved ? <ShieldCheck size={15} aria-hidden="true" /> : <ShieldX size={15} aria-hidden="true" />}
            {isApproved ? 'Allowed' : 'Blocked'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => onPermissionResponse?.(perm.permission_id, 'allow')}
              style={{ height: 38, padding: '0 1rem', fontSize: '1rem', fontWeight: 600 }}
            >
              <ShieldCheck size={15} aria-hidden="true" />
              Allow
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => onPermissionResponse?.(perm.permission_id, 'deny')}
              style={{ height: 38, padding: '0 0.875rem', fontSize: '1rem', fontWeight: 500, border: '1px solid var(--color-border)' }}
            >
              <ShieldX size={15} aria-hidden="true" />
              Block
            </button>
          </div>
        )}
      </div>
    )
  }

  // A progress line rendered on its own (the docks normally fold these into a
  // turn's "Show steps"; this is the fallback rendering).
  if (message.role === 'activity') {
    const isWarning = message.activityLevel === 'warning'
    const metaText = formatActivityMeta(message.meta)
    return (
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.5rem',
        fontSize: '1rem',
        lineHeight: 1.5,
        color: isWarning ? 'var(--color-warning)' : 'var(--color-text-secondary)',
      }}>
        {isWarning
          ? <AlertTriangle size={15} aria-hidden="true" style={{ flexShrink: 0, marginTop: 3 }} />
          : <Info size={15} aria-hidden="true" style={{ flexShrink: 0, marginTop: 3 }} />}
        <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{message.content}</span>
        {metaText && (
          <span style={{ flexShrink: 0, fontSize: '0.875rem', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
            {metaText}
          </span>
        )}
      </div>
    )
  }

  // Tool activity message (compact)
  if (message.role === 'tool') {
    const label = message.tool ? (TOOL_LABELS[message.tool] || message.tool) : 'Working'
    const file = message.file || ''
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        fontSize: '1rem',
        color: 'var(--color-text-secondary)',
      }}>
        <span>{label}</span>
        {file && (
          <span style={{
            fontFamily: 'var(--font-mono)',
            fontSize: '0.875rem',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {file}
          </span>
        )}
      </div>
    )
  }

  // System note ("Stopped", "Run ended") — a quiet centred line.
  if (message.role === 'system') {
    return (
      <div style={{
        fontSize: '0.9375rem',
        color: 'var(--color-text-secondary)',
        textAlign: 'center',
        overflowWrap: 'anywhere',
      }}>
        {message.content.replace(/^\[(.*)\]$/, '$1')}
      </div>
    )
  }

  // User message
  if (message.role === 'user') {
    return <UserBubble>{message.content}</UserBubble>
  }

  // Assistant message. Strip any twasta:ask sentinel block (surfaced as its own
  // `ask` bubble); if nothing else remains, render nothing.
  const assistantText = stripAskBlock(message.content)
  if (!assistantText) return null
  return <AssistantText text={assistantText} label={agentLabel} />
}
